import { useId, useState, type CSSProperties, type ReactNode } from 'react';
import { Collapse, Tooltip, UnstyledButton } from '@mantine/core';
import { IconChevronDown } from '@tabler/icons-react';
import { PageHeader } from '@/components/PageHeader';
import type { PageHeaderProps } from '@/components/PageHeader';
import classes from './DashboardCollapsibleSection.module.css';

export type DashboardCollapsibleSectionProps = {
  title: ReactNode;
  accentColor: string;
  expandLabel: string;
  collapseLabel: string;
  mb?: PageHeaderProps['mb'];
  mt?: PageHeaderProps['mt'];
  children: ReactNode;
};

/** Section with chevron collapse. Always starts expanded; preference is not persisted. */
export function DashboardCollapsibleSection({
  title,
  accentColor,
  expandLabel,
  collapseLabel,
  mb = 15,
  mt = 10,
  children,
}: DashboardCollapsibleSectionProps) {
  const [expanded, setExpanded] = useState(true);
  const panelId = useId();

  const rootStyle = {
    '--section-accent': accentColor,
  } as CSSProperties;

  return (
    <section style={rootStyle}>
      <PageHeader
        variant="section"
        title={title}
        accentColor={accentColor}
        mb={expanded ? mb : 10}
        mt={mt}
        actions={
          <Tooltip
            label={expanded ? collapseLabel : expandLabel}
            withArrow
            position="left"
          >
            <UnstyledButton
              type="button"
              className={`${classes.toggleBtn} ${expanded ? classes.toggleBtnOpen : ''}`}
              onClick={() => setExpanded((v) => !v)}
              aria-expanded={expanded}
              aria-controls={panelId}
              aria-label={expanded ? collapseLabel : expandLabel}
            >
              <IconChevronDown size={18} stroke={2} />
            </UnstyledButton>
          </Tooltip>
        }
      />

      <Collapse in={expanded} transitionDuration={280}>
        <div id={panelId}>{children}</div>
      </Collapse>
    </section>
  );
}
